import React, { useState, useMemo } from 'react';
import { Card, Collapse, IconButton } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import Icon from '@mui/material/Icon';

function CPQAttributesTab({ product, renderFieldsProp }) {
  const [expandedItem, setExpandedItem] = useState({});

  const handleItemClick = (itemId) => {
    setExpandedItem((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const processAttributes = useMemo(() => {
    if (!product?.related_data)
      return { configAttributes: [], classificationAttributes: [], productOptionAttributes: [] };

    const relatedData = product.related_data;

    // Process Configuration Attributes (both top-level and from productAttributes)
    const configAttributes = [];

    // Top-level configuration attributes
    (relatedData.configurationAttributes || []).forEach((attr, index) => {
      configAttributes.push({
        id: `config-${index}`,
        name: attr.name || 'Configuration Attribute',
        fields: attr.fields || {},
        type: 'Configuration Attribute',
      });
    });

    // Configuration Product Attributes from productAttributes
    (relatedData.productAttributes || []).forEach((attrGroup, groupIndex) => {
      (attrGroup.configurationProductAttributes || []).forEach((attr, index) => {
        configAttributes.push({
          id: `pa-config-${groupIndex}-${index}`,
          name: attr.name || 'Configuration Product Attribute',
          fields: attr.fields || {},
          type: 'Configuration Product Attribute',
        });
      });
    });

    // Process Classification Attributes
    const classificationAttributes = (relatedData.classificationAttributes || []).map((attr, index) => ({
      id: `classification-${index}`,
      name: attr.name || 'Classification Attribute',
      fields: attr.fields || {},
      type: 'Classification Attribute',
    }));

    // Process Product Option Attributes with proper hierarchy
    const productOptionAttributes = [];
    (relatedData.productAttributes || []).forEach((attrGroup, groupIndex) => {
      (attrGroup.productOptionAttributes || []).forEach((option, optionIndex) => {
        const optionName =
          option.IDFieldList?.['SBQQ__ProductName__c'] ||
          option.name ||
          `Option: ${option.sfid || option.id || `Option ${optionIndex + 1}`}`;

        // Only create product option if it has meaningful content
        if (!optionName || optionName === 'Product Option') {
          return;
        }

        const productOptionAttribute = {
          id: `product-option-attr-${groupIndex}-${optionIndex}`,
          name: optionName,
          fields: option.fields || {},
          type: 'Product Option Attribute',
          attributeSets: [],
        };

        // Process attribute sets for this product option
        (option.productAttributeSets || []).forEach((productAttrSet, setIndex) => {
          const setName = productAttrSet.attributeSets?.name || `Attribute Set ${setIndex + 1}`;

          const attributeSet = {
            id: `attr-set-${groupIndex}-${optionIndex}-${setIndex}`,
            name: setName,
            fields: productAttrSet.fields || {},
            type: 'Attribute Set',
            attributeItems: [],
          };

          // Process attribute items for this attribute set
          if (productAttrSet.attributeSets?.attributeItems) {
            productAttrSet.attributeSets.attributeItems.forEach((item, itemIndex) => {
              // Use a more descriptive name for the attribute item
              const itemName = item.name || item.IDFieldList?.Name || `Attribute Item ${itemIndex + 1}`;

              console.log('item', item);
              const attributeItem = {
                id: `attr-item-${groupIndex}-${optionIndex}-${setIndex}-${itemIndex}`,
                name: itemName,
                fields: item.fields || {},
                type: 'Attribute Item',
                configurationAttributes: item.configurationItemAttributes
                  ? {
                      id: `config-${groupIndex}-${optionIndex}-${setIndex}-${itemIndex}`,
                      name: item.configurationItemAttributes.name || 'Configuration Item Attribute',
                      fields: item.configurationItemAttributes.fields || {},
                      type: 'Configuration Item Attribute',
                    }
                  : null,
              };

              attributeSet.attributeItems.push(attributeItem);
            });
          }

          productOptionAttribute.attributeSets.push(attributeSet);
        });

        productOptionAttributes.push(productOptionAttribute);
      });
    });

    return {
      configAttributes,
      classificationAttributes,
      productOptionAttributes,
    };
  }, [product]);

  const renderAttributeCard = (attribute) => {
    const filteredFields = Object.keys(attribute.fields || {})
      .filter((key) => key && attribute.fields[key] !== null && attribute.fields[key] !== undefined)
      .reduce((obj, key) => ({ ...obj, [key]: attribute.fields[key] }), {});

    return (
      <Card sx={{ marginBottom: '1rem' }} key={attribute.id}>
        <ArgonBox mb={0.5} mt={0.5}>
          <ArgonBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => handleItemClick(attribute.id)}
            style={{ cursor: 'pointer' }}
            p={1}
            borderRadius="md"
          >
            <ArgonTypography variant="h6" fontWeight="medium" color="secondary" ml={1}>
              {attribute.name}
            </ArgonTypography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleItemClick(attribute.id);
              }}
            >
              <Icon>{expandedItem[attribute.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
            </IconButton>
          </ArgonBox>
          <Collapse in={expandedItem[attribute.id]} timeout="auto" unmountOnExit>
            <ArgonBox p={2} sx={{ borderRadius: 'md' }}>
              {Object.keys(filteredFields).length > 0 && renderFieldsProp(filteredFields)}

              {/* Render children if they exist */}
              {attribute.attributeSets &&
                attribute.attributeSets.map((attrSet) => (
                  <ArgonBox key={attrSet.id} mt={2}>
                    <Card sx={{ marginBottom: '0.5rem' }}>
                      <ArgonBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        onClick={() => handleItemClick(attrSet.id)}
                        style={{ cursor: 'pointer' }}
                        p={1}
                        borderRadius="md"
                      >
                        <ArgonTypography variant="body2" fontWeight="medium" color="secondary" ml={1}>
                          {attrSet.name}
                        </ArgonTypography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleItemClick(attrSet.id);
                          }}
                        >
                          <Icon>{expandedItem[attrSet.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                        </IconButton>
                      </ArgonBox>
                      <Collapse in={expandedItem[attrSet.id]} timeout="auto" unmountOnExit>
                        <ArgonBox p={2}>
                          {Object.keys(attrSet.fields || {}).length > 0 && renderFieldsProp(attrSet.fields)}

                          {/* Render attribute items */}
                          {attrSet.attributeItems &&
                            attrSet.attributeItems.map((item) => (
                              <ArgonBox key={item.id} mt={2}>
                                <Card sx={{ marginBottom: '0.5rem' }}>
                                  <ArgonBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    onClick={() => handleItemClick(item.id)}
                                    style={{ cursor: 'pointer' }}
                                    p={1}
                                    borderRadius="md"
                                  >
                                    <ArgonTypography variant="body2" fontWeight="medium" color="secondary" ml={1}>
                                      {item.name}
                                    </ArgonTypography>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleItemClick(item.id);
                                      }}
                                    >
                                      <Icon>{expandedItem[item.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                                    </IconButton>
                                  </ArgonBox>
                                  <Collapse in={expandedItem[item.id]} timeout="auto" unmountOnExit>
                                    <ArgonBox p={2}>
                                      {Object.keys(item.fields || {}).length > 0 && renderFieldsProp(item.fields)}

                                      {/* Render configuration attributes */}
                                      {item.configurationAttributes && (
                                        <ArgonBox mt={2}>
                                          <Card sx={{ marginBottom: '0.5rem' }}>
                                            <ArgonBox
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="space-between"
                                              onClick={() => handleItemClick(item.configurationAttributes.id)}
                                              style={{ cursor: 'pointer' }}
                                              p={1}
                                              borderRadius="md"
                                            >
                                              <ArgonTypography
                                                variant="body2"
                                                fontWeight="medium"
                                                color="secondary"
                                                ml={1}
                                              >
                                                {item.configurationAttributes.name}
                                              </ArgonTypography>
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleItemClick(item.configurationAttributes.id);
                                                }}
                                              >
                                                <Icon>
                                                  {expandedItem[item.configurationAttributes.id]
                                                    ? 'keyboard_arrow_up'
                                                    : 'keyboard_arrow_down'}
                                                </Icon>
                                              </IconButton>
                                            </ArgonBox>
                                            <Collapse
                                              in={expandedItem[item.configurationAttributes.id]}
                                              timeout="auto"
                                              unmountOnExit
                                            >
                                              <ArgonBox p={2}>
                                                {Object.keys(item.configurationAttributes.fields || {}).length > 0 &&
                                                  renderFieldsProp(item.configurationAttributes.fields)}
                                              </ArgonBox>
                                            </Collapse>
                                          </Card>
                                        </ArgonBox>
                                      )}
                                    </ArgonBox>
                                  </Collapse>
                                </Card>
                              </ArgonBox>
                            ))}
                        </ArgonBox>
                      </Collapse>
                    </Card>
                  </ArgonBox>
                ))}
            </ArgonBox>
          </Collapse>
        </ArgonBox>
      </Card>
    );
  };

  const renderAttributeSection = (title, attributes) => {
    if (!attributes || attributes.length === 0) {
      return null;
    }

    return (
      <ArgonBox mb={3}>
        <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
          {title}
        </ArgonTypography>
        {attributes.map(renderAttributeCard)}
      </ArgonBox>
    );
  };

  const { configAttributes, classificationAttributes, productOptionAttributes } = processAttributes;

  if (configAttributes.length === 0 && classificationAttributes.length === 0 && productOptionAttributes.length === 0) {
    return (
      <ArgonBox p={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
              No attributes data available for this product.
            </ArgonTypography>
          </ArgonBox>
        </Card>
      </ArgonBox>
    );
  }

  return (
    <ArgonBox p={3}>
      {renderAttributeSection('Configuration Attributes', configAttributes)}
      {renderAttributeSection('Classification Attributes', classificationAttributes)}
      {renderAttributeSection('Product Option Attributes', productOptionAttributes)}
    </ArgonBox>
  );
}

export default CPQAttributesTab;
